import _ from 'lodash';

import {
  appraisalOperationOptions,
  AppraisalOperationType,
  appraisalQcOperationOptions,
  AppraisalQcOperationType,
  appraisalResubmitModeOptions,
  areAllCreditBureausSelected,
  argyleDuplicatePolicyOptions,
  argyleReportTypeOptions,
  avmVendorTypeMap,
  cascadeProviderMap,
  creditOrderActionSubtypes,
  creditPullTypeOptions,
  creditReportActionSubtypeOptions,
  CreditReportField,
  DEFAULT_APPRAISAL_DUE_DATE_BUSINESS_DAYS,
  defaultCreditPullTypeOption,
  defaultFloodServiceOperationOption,
  defaultHalcyonAuthorizationSourceOption,
  defaultHalcyonResubmitModeOption,
  defaultRunLoanCalculationsSaveModeOption,
  fhaServiceTypeModeOptionsMap,
  fhaServiceTypeOptions,
  FieldTypesEnum,
  finicityDuplicatePolicyOptions,
  floodDeterminationUpdateKindOptions,
  floodElevationCertificateProviderOptions,
  FloodElevationCertificateProviderType,
  floodServiceDeterminationKinds,
  FloodServiceKind,
  floodServiceKindOptions,
  floodServiceOperationOptions,
  getDefaultHomeVisionResubmitModeOption,
  halcyonAuthorizationSourceOptions,
  HalcyonBorrowerTargetEnum,
  halcyonBorrowerTargetOptions,
  HalcyonMethodEnum,
  halcyonMethodOptions,
  HalcyonMultiYearEnum,
  halcyonResubmitModeOptions,
  halcyonYearModeOptions,
  homeVisionResubmitModeOptions,
  homeVisionSupportingDocTypeOptions,
  iframeSizeOptions,
  LoanActionDateTypesEnum,
  LoanActionStaticItemsTypesEnum,
  LoanActionStaticValueSourceEnum,
  LoanEditorActionsEnum,
  normalizeCreditBureaus,
  ReportTypeEnum,
  runLoanCalculationsSaveModeOptions,
  taskTypeNameExclusionList,
  vaServiceTypeOptions,
  verificationOfAssetsOptions,
} from 'constants/loanActionEditor';
import { AusType } from 'constants/loanApplication';
import { loanContactTypesOptions, loanSenderTypesOptions, ucdTypeOptions } from 'constants/options';
import { TaskAssignmentType, taskAssignmentTypeOptions } from 'constants/tasks';
import { getConditionalObject } from 'utils/data';
import { logger } from 'utils/logger';
import { findPriorToOption, getPriorToRequestFields } from 'utils/priorTo';
import { extendObjWithUUID } from 'utils/uuid';

import { COMPANY_DEFAULT_FLOOD_PRODUCT_OPTION } from './LoanActionFloodServiceRow.constants';
import { replaceSpanWithBrackets } from './LoanActionsGroup.utils';
import {
  getAssignTaskBorrowerTargetsApiFormat,
  getAssignTaskBorrowerTargetsFormFormat,
  getAssignTaskEntityAssignmentTypeOption,
} from './LoanActionTaskRow.utils';
import {
  formatActionItemsFieldsInitialFormValues,
  formatActionItemsFieldsOutputFormValues,
  getApplicationImpoundApiFormat,
  getApplicationImpoundFormItem,
  getContactFormFormatFromApiFormat,
  getCreateDocumentFromSourceFormItem,
  getDocumentTypeSelectionBodyToSend,
  getGeneratePdfSourceTypeBodyToSend,
  getMailDocumentPackageApiFormat,
  getMailDocumentPackageFormItem,
  getMismoDocumentFormItem,
  getPdfSourceFormItem,
  getRunFhaServiceFormItem,
  getSavePendingChangesFormItem,
  getSetApplicationValueItemType,
  getSetFormBundleReadinessStatusFormItem,
  getUploadFormDeliveryApiFormat,
  getUploadFormDeliveryFormItem,
} from './LoanActionTransformationHelpers.utils';
import {
  getAiProcessActionApiFormat,
  getRunAiProcessFormFormat,
  getSendAiProcessResultsFormFormat,
} from './LoanActionTransformations.aiProcess';
import { getSendEmailTemplateApiFormat } from './LoanActionTransformations.email';
import {
  getCallIntegrationApiFormat,
  getCallIntegrationFormFormat,
} from './LoanActionTransformations.integration';
import {
  getMersActionApiFormat,
  getMersActionFormFormat,
  getMersPreviewApiFormat,
  getMersPreviewFormFormat,
  MERS_ACTION_TYPES,
} from './LoanActionTransformations.mers';
import {
  getMortgageInsuranceApiFormat,
  getMortgageInsuranceFormFormat,
} from './LoanActionTransformations.mortgageInsurance';
import {
  getSnapdocsActionApiFormat,
  getSnapdocsActionFormFormat,
  SNAPDOCS_PAYLOAD_ACTION_TYPES,
} from './LoanActionTransformations.snapdocs';
import {
  getSubCompanyActionApiFormat,
  getSubCompanyActionFormFormat,
  getSubCompanyRecipientsFormFormat,
} from './LoanActionTransformations.subCompany';

const PAYLOAD_LESS_ACTION_TYPES = new Set([
  LoanEditorActionsEnum.CONVERT_LEAD_TO_LOAN,
  LoanEditorActionsEnum.SUBMIT_APPLICATION,
  LoanEditorActionsEnum.START_CERTIFICATION_CYCLE,
  LoanEditorActionsEnum.FINALIZE_DO,
  LoanEditorActionsEnum.ASSIGN_CONDITIONED_TASK_TO_BORROWER,
  LoanEditorActionsEnum.IMPERSONATE_AS_BORROWER,
  LoanEditorActionsEnum.RETRY_HALCYON_ORDER,
  LoanEditorActionsEnum.RESEND_HALCYON_AUTHORIZATION,
  LoanEditorActionsEnum.RESEND_HALCYON_CONSENT_DOCUMENTS,
  LoanEditorActionsEnum.CANCEL_HALCYON_ORDER,
  LoanEditorActionsEnum.GENERATE_COMPLIANCE_REPORT,
  LoanEditorActionsEnum.RUN_COMPLIANCE_AUDIT,
  LoanEditorActionsEnum.SET_FORM_DELIVERY_SENT_DATE,
  LoanEditorActionsEnum.MOVE_BACK_MILESTONE,
  LoanEditorActionsEnum.REFRESH_CREDIT_EVENTS,
  LoanEditorActionsEnum.OPEN_PROGRAM_SELECTION,
  LoanEditorActionsEnum.CANCEL_SNAPDOCS_CLOSING,
  LoanEditorActionsEnum.COMPLETE_SNAPDOCS_CLOSING,
  LoanEditorActionsEnum.REOPEN_SNAPDOCS_CLOSING,
  LoanEditorActionsEnum.UPDATE_SNAPDOCS_APPOINTMENT,
  LoanEditorActionsEnum.OPT_IN_SNAPDOCS_ESIGNING,
  LoanEditorActionsEnum.IMPORT_MISMO_TO_LOAN,
  LoanEditorActionsEnum.LOCK_LOAN,
]);

const EXTRACTED_FORM_FORMAT_GETTERS = {
  [LoanEditorActionsEnum.CALL_INTEGRATION]: getCallIntegrationFormFormat,
  [LoanEditorActionsEnum.ORDER_MORTGAGE_INSURANCE]: getMortgageInsuranceFormFormat,
  [LoanEditorActionsEnum.CHECK_MORTGAGE_INSURANCE_STATUS]: getMortgageInsuranceFormFormat,
  [LoanEditorActionsEnum.ACTIVATE_MORTGAGE_INSURANCE]: getMortgageInsuranceFormFormat,
  [LoanEditorActionsEnum.RUN_AI_PROCESS]: getRunAiProcessFormFormat,
  [LoanEditorActionsEnum.SEND_AI_PROCESS_RESULTS]: getSendAiProcessResultsFormFormat,
  ...Object.fromEntries(
    SNAPDOCS_PAYLOAD_ACTION_TYPES.map(type => [type, getSnapdocsActionFormFormat]),
  ),
  ...Object.fromEntries(MERS_ACTION_TYPES.map(type => [type, getMersActionFormFormat])),
  [LoanEditorActionsEnum.PREVIEW_MERS]: getMersPreviewFormFormat,
};

const getOptionValue = option => option?.value;

export const getConditionName = condition =>
  condition ? `${condition.number} - ${condition.message}` : '';

export const cleanTaskTypeName = taskTypeName =>
  taskTypeName
    ? taskTypeNameExclusionList.reduce((acc, exclusion) => acc.replace(exclusion, ''), taskTypeName)
    : '';

const expandHalcyonFormsToApiFormat = (ruleForms, yearMode, specificYears) => {
  const yearModeValue = yearMode?.value;
  const isSpecific = yearModeValue === HalcyonMultiYearEnum.SPECIFIC;
  const years = specificYears?.map(y => y.value) ?? [];

  if (isSpecific) {
    return ruleForms.flatMap(f =>
      years.map(y => ({ formType: f.value, year: y, multiYear: null })),
    );
  }

  return ruleForms.map(f => ({
    formType: f.value,
    year: null,
    multiYear: yearModeValue ?? null,
  }));
};

const parseHalcyonRuleFormsToFormState = (rule, halcyonFormOptions) => {
  const ruleForms = rule?.forms || [];
  const firstForm = ruleForms[0];
  const isSpecific = !_.isNil(firstForm?.year);

  const distinctFormTypes = _.uniq(ruleForms.map(f => f.formType));
  const formOptions = distinctFormTypes
    .map(ft => halcyonFormOptions.find(({ value }) => value === ft))
    .filter(Boolean);

  if (isSpecific) {
    const distinctYears = _.uniq(ruleForms.map(f => f.year).filter(y => !_.isNil(y)));

    return {
      forms: formOptions,
      yearMode: halcyonYearModeOptions.find(({ value }) => value === HalcyonMultiYearEnum.SPECIFIC),
      specificYears: distinctYears.map(y => ({ name: y, value: y })),
    };
  }

  const multiYear = firstForm?.multiYear;

  return {
    forms: formOptions,
    yearMode: multiYear ? halcyonYearModeOptions.find(({ value }) => value === multiYear) : null,
    specificYears: [],
  };
};

const getCreditReportApiFormat = ({
  reportValue,
  creditActionSubtype,
  creditBureaus,
  creditPullType,
  creditAllowManualSelection,
  isAutoAction,
}) => {
  const creditType = `${reportValue}.${creditActionSubtype.value}`;

  if (!creditOrderActionSubtypes.includes(creditActionSubtype.value)) {
    return { type: creditType };
  }

  const bureaus = normalizeCreditBureaus(creditBureaus);

  return {
    type: creditType,
    ...getConditionalObject(!areAllCreditBureausSelected(bureaus), { bureaus }),
    pullType: creditPullType?.value ?? defaultCreditPullTypeOption.value,
    allowManualSelection: !isAutoAction && !!creditAllowManualSelection,
  };
};

const getCreditReportFormItem = ({
  type,
  bureaus,
  pullType,
  allowManualSelection,
  reportsOptions,
}) => {
  const [reportType, creditActionSubtype] = type.split('.');

  const reportOption = reportsOptions.find(({ value }) => value === reportType);
  const creditActionSubtypeOption = creditReportActionSubtypeOptions.find(
    ({ value }) => value === creditActionSubtype,
  );

  return {
    report: reportOption,
    creditActionSubtype: creditActionSubtypeOption,
    ...getConditionalObject(creditOrderActionSubtypes.includes(creditActionSubtype), () => ({
      creditBureaus: normalizeCreditBureaus(bureaus),
      creditPullType:
        creditPullTypeOptions.find(({ value }) => value === pullType) ??
        defaultCreditPullTypeOption,
      creditAllowManualSelection: !!allowManualSelection,
    })),
  };
};

export const getActionApiFormatFromFormFormat = ({
  formItem,
  loanActionFieldsMap,
  loanRoleCriteria,
  buildExpressionTree,
  chatCriteria,
  closingPortalEmailExpression,
  savePendingChangesPermissions,
  arithmeticOperationsSchemaParams,
  isTimedSequence,
  index,
  timedSequenceSchemaParams,
  halcyonExpressionSchemaParams,
  dynamicFieldsOptions,
  isNewExpressionsParsingLogicEnabled,
}) => {
  const buildHalcyonExpression = userFriendlyExpression =>
    buildExpressionTree(userFriendlyExpression, {
      schemaParams: halcyonExpressionSchemaParams,
      isNewExpressionsParsingLogicEnabled,
    });
  const type = formItem.itemType.value;

  const stopExpression =
    isTimedSequence && formItem.timedSequenceOptions.userFriendlyStopExpression
      ? {
          value: buildExpressionTree(formItem.timedSequenceOptions.userFriendlyStopExpression, {
            schemaParams: timedSequenceSchemaParams,
            isNewExpressionsParsingLogicEnabled,
          }),
          userFriendlyValue: formItem.timedSequenceOptions.userFriendlyStopExpression,
        }
      : null;
  const timedSequenceOptions = isTimedSequence
    ? {
        ...formItem.timedSequenceOptions,
        stopExpression,
      }
    : null;

  const common = {
    type,
    timedSequenceOptions,
    executionOrder: index,
  };

  if (PAYLOAD_LESS_ACTION_TYPES.has(type)) {
    return common;
  }

  if (SNAPDOCS_PAYLOAD_ACTION_TYPES.includes(type)) {
    return getSnapdocsActionApiFormat(formItem, common);
  }

  if (MERS_ACTION_TYPES.includes(type)) {
    return getMersActionApiFormat(formItem, common);
  }

  if (type === LoanEditorActionsEnum.PREVIEW_MERS) {
    return getMersPreviewApiFormat(formItem, common);
  }

  switch (type) {
    case LoanEditorActionsEnum.ADD_TAG:

    case LoanEditorActionsEnum.REMOVE_TAG: {
      return { ...common, tagIds: formItem.tags.map(getOptionValue) };
    }

    case LoanEditorActionsEnum.GENERATE_CHECKLIST: {
      return { ...common, checklistIds: formItem.checklists.map(getOptionValue) };
    }

    case LoanEditorActionsEnum.GENERATE_FORM:

    case LoanEditorActionsEnum.GENERATE_AND_SAVE_FORM: {
      return { ...common, formIds: formItem.forms.map(getOptionValue) };
    }

    case LoanEditorActionsEnum.GENERATE_PDF: {
      const pdfForm = formItem.pdf[0];

      return {
        ...common,
        sourceType: getGeneratePdfSourceTypeBodyToSend(pdfForm),
      };
    }

    case LoanEditorActionsEnum.CREATE_DOCUMENT_FROM_SOURCE: {
      const documentForm = formItem.createDocumentFromSource[0];

      return {
        ...common,
        sourceType: getGeneratePdfSourceTypeBodyToSend(documentForm),
        documentType: getDocumentTypeSelectionBodyToSend(documentForm),
      };
    }

    case LoanEditorActionsEnum.SET_FORM_BUNDLE_READINESS_STATUS: {
      const { readinessStatus, formBundleIds } = formItem.setFormBundleReadinessStatus[0];
      const selectedBundleIds = formBundleIds?.map(getOptionValue) ?? [];

      return {
        ...common,
        targetReadinessStatus: readinessStatus?.value ?? null,
        formBundleIds: selectedBundleIds.length ? selectedBundleIds : null,
      };
    }

    case LoanEditorActionsEnum.SEND_FORM: {
      return {
        ...common,
        forms: formItem.forms.map(({ value, isTest }) => ({ id: value, isTest: !!isTest })),
      };
    }

    case LoanEditorActionsEnum.SEND_FORM_BUNDLE: {
      return {
        ...common,
        formBundles: formItem.formBundles.map(
          ({ value, isTest, requiresApproval, enablePreview, formForPreview }) => ({
            id: value,
            isTest: !!isTest,
            requiresApproval: !!requiresApproval,
            previewType: enablePreview
              ? { type: 'Form', formId: formForPreview.value }
              : { type: 'None' },
          }),
        ),
      };
    }

    case LoanEditorActionsEnum.MAIL_DOCUMENT_PACKAGE: {
      return getMailDocumentPackageApiFormat({ formItem, common });
    }

    case LoanEditorActionsEnum.UPLOAD_FORM_DELIVERY: {
      return getUploadFormDeliveryApiFormat({ formItem, common });
    }

    case LoanEditorActionsEnum.ORDER_UPDATE_REPORT_AUTO:

    case LoanEditorActionsEnum.ORDER_UPDATE_REPORT_MANUAL: {
      const isAutoAction = type === LoanEditorActionsEnum.ORDER_UPDATE_REPORT_AUTO;

      return {
        ...common,
        reportTypes: formItem.reports.map(
          ({
            report,
            creditActionSubtype,
            creditBureaus,
            creditPullType,
            creditAllowManualSelection,
            vendorOptions,
            argyleReportType,
            floodElevationCertificateProvider,
            serviceLinkOperation,
            serviceLinkProductOverride,
            serviceLinkRush,
            serviceLinkAllowManualSelection,
            verificationOfAssets,
            halcyonMethod,
            halcyonBaseForms,
            halcyonBaseYearMode,
            halcyonBaseSpecificYears,
            halcyonUseConditionalRules,
            halcyonConditionalRules,
            halcyonBorrowerTarget,
            halcyonBorrowerExpression,
            halcyonAllowManualSelection,
            halcyonResubmitMode,
            halcyonAuthorizationSource,
            halcyonUseNonProdCredentials,
            halcyonIsPreAuthorization,
          }) => {
            const reportValue = report.value;

            if (reportValue === CreditReportField.value) {
              return getCreditReportApiFormat({
                reportValue,
                creditActionSubtype,
                creditBureaus,
                creditPullType,
                creditAllowManualSelection,
                isAutoAction,
              });
            }

            if (reportValue === ReportTypeEnum.AVM) {
              const enableCascadeOnHighFsd = !!vendorOptions?.enableCascadeOnHighFsd;

              return {
                type: reportValue,
                ...getConditionalObject(vendorOptions, () => ({
                  vendorOptions: {
                    orderedCascadeProviders: enableCascadeOnHighFsd
                      ? vendorOptions?.orderedCascadeProviders?.map(({ value }) => value)
                      : null,
                    enableCascadeOnHighFsd,
                    maximumFsdReturned: Number(vendorOptions?.maximumFsdReturned),
                    type: avmVendorTypeMap[vendorOptions?.vendorType?.value]?.value,
                  },
                })),
              };
            }

            if (reportValue === ReportTypeEnum.ARGYLE_VERIFICATION) {
              return {
                type: 'ArgyleIncomeVerification',
                reportType: argyleReportType?.value,
              };
            }

            if (reportValue === ReportTypeEnum.FLOOD_ELEVATION_CERTIFICATE) {
              const providerValue = floodElevationCertificateProvider?.value;
              const isServiceLink =
                providerValue === FloodElevationCertificateProviderType.SERVICE_LINK;
              const operationValue =
                serviceLinkOperation?.value ?? defaultFloodServiceOperationOption.value;
              const isDeterminationOperation = operationValue === FloodServiceKind.DETERMINATION;
              const isOrderingOperation = floodServiceDeterminationKinds.includes(operationValue);
              // "Allow order options at execution time" defers Product + Rush to the operator at
              // click time, so the UI hides both while it is on — mirror that here by clearing them.
              const allowManualActive =
                isDeterminationOperation && !isAutoAction && !!serviceLinkAllowManualSelection;

              return {
                type: 'FloodElevationCertificate',
                provider: providerValue,
                ...getConditionalObject(isServiceLink, () => ({
                  options: {
                    type: FloodElevationCertificateProviderType.SERVICE_LINK,
                    operation: operationValue,
                    // Product/Rush are operation-gated in the UI AND hidden while Allow-manual is on;
                    // gate them here too so a stale value can never serialize onto an operation (or an
                    // execution-time-prompt config) that does not expose it.
                    productOverrideKey:
                      isDeterminationOperation && !allowManualActive
                        ? (serviceLinkProductOverride?.value ?? null)
                        : null,
                    rush: isOrderingOperation && !allowManualActive && !!serviceLinkRush,
                    allowManualSelection: allowManualActive,
                  },
                })),
              };
            }

            if (reportValue === ReportTypeEnum.VERIFICATION_OF_ASSETS) {
              return {
                type: reportValue,
                provider: {
                  type: verificationOfAssets?.value,
                },
              };
            }

            if (reportValue === ReportTypeEnum.HALCYON_TAX_TRANSCRIPT) {
              const resubmitModeValue =
                halcyonResubmitMode?.value ?? defaultHalcyonResubmitModeOption.value;
              const authorizationSourceValue =
                halcyonAuthorizationSource?.value ?? defaultHalcyonAuthorizationSourceOption.value;
              const useNonProductionCredentials = !!halcyonUseNonProdCredentials;
              const isPreAuthorization = !!halcyonIsPreAuthorization;

              if (halcyonAllowManualSelection && !isAutoAction) {
                return {
                  type: reportValue,
                  allowManualSelection: true,
                  resubmitMode: resubmitModeValue,
                  authorizationSource: authorizationSourceValue,
                  useNonProductionCredentials,
                  isPreAuthorization,
                };
              }

              // IRS Account pre-authorization orders no transcripts, so the form pickers are
              // hidden — drop any stale form state instead of sending it.
              const isPreAuthWithoutForms =
                isPreAuthorization && halcyonMethod?.value === HalcyonMethodEnum.IRS_ACCOUNT;

              const baseRule = {
                expression: null,
                userFriendlyExpression: null,
                forms: isPreAuthWithoutForms
                  ? []
                  : expandHalcyonFormsToApiFormat(
                      halcyonBaseForms || [],
                      halcyonBaseYearMode,
                      halcyonBaseSpecificYears,
                    ),
              };

              const conditionalRules =
                halcyonUseConditionalRules && !isPreAuthWithoutForms
                  ? (halcyonConditionalRules || []).map(rule => ({
                      expression: buildHalcyonExpression(rule.expression),
                      userFriendlyExpression: rule.expression,
                      forms: expandHalcyonFormsToApiFormat(
                        rule.forms || [],
                        rule.yearMode,
                        rule.specificYears,
                      ),
                    }))
                  : [];

              const isByExpressionTarget =
                halcyonBorrowerTarget?.value === HalcyonBorrowerTargetEnum.BY_EXPRESSION;

              const borrowerTargetForApi = isByExpressionTarget
                ? {
                    type: HalcyonBorrowerTargetEnum.BY_EXPRESSION,
                    expression: buildHalcyonExpression(halcyonBorrowerExpression),
                    userFriendlyExpression: halcyonBorrowerExpression,
                  }
                : { type: halcyonBorrowerTarget?.value };

              return {
                type: reportValue,
                method: halcyonMethod?.value,
                formYearRules: [baseRule, ...conditionalRules],
                resubmitMode: resubmitModeValue,
                authorizationSource: authorizationSourceValue,
                useNonProductionCredentials,
                isPreAuthorization,
                ...getConditionalObject(!!halcyonBorrowerTarget, {
                  borrowerTarget: borrowerTargetForApi,
                }),
              };
            }

            return { type: reportValue };
          },
        ),
      };
    }

    case LoanEditorActionsEnum.SET_FIELD_STATIC: {
      return {
        ...common,
        changes: formItem.fields.map(
          ({ field, valueSource, value, dateType, sourceField, sequenceKey, digitCount, seed }) => {
            const isCurrentDate = dateType === LoanActionDateTypesEnum.EXECUTION_DATE;
            const isFieldSource = valueSource === LoanActionStaticValueSourceEnum.FIELD;
            const isNextSequence = valueSource === LoanActionStaticValueSourceEnum.NEXT_SEQUENCE;
            const fieldParams = loanActionFieldsMap[field.value];
            const correctValue =
              isCurrentDate || isFieldSource || isNextSequence
                ? undefined
                : formatActionItemsFieldsOutputFormValues({
                    fieldValue: value,
                    valueData: fieldParams,
                  });

            return {
              type: getSetApplicationValueItemType({ valueSource, dateType, fieldParams }),
              fieldId: field.value,
              newValue: correctValue,
              sourceFieldId: isFieldSource ? sourceField.value : undefined,
              sequenceKey: isNextSequence ? sequenceKey : undefined,
              digitCount: isNextSequence ? Number(digitCount) : undefined,
              seed: isNextSequence ? Number(seed) : undefined,
            };
          },
        ),
      };
    }

    case LoanEditorActionsEnum.SET_FIELD_DYNAMIC: {
      const changes = formItem.variables.map(({ field, isRequired, name }) => ({
        required: isRequired,
        type: LoanActionStaticItemsTypesEnum.DYNAMIC_VALUE,
        fieldId: field.value,
        name,
      }));

      return { ...common, changes };
    }

    case LoanEditorActionsEnum.ENTER_CUSTOM_FIELDS: {
      const selectedOptions = formItem.enterCustomFields?.[0]?.customFieldDefinitionIds ?? [];
      const customFieldDefinitionIds = selectedOptions.map(option => option?.value ?? option);

      return { ...common, customFieldDefinitionIds };
    }

    case LoanEditorActionsEnum.RUN_AUS: {
      const selectedAusType = formItem.runAusSubmit?.[0].value;
      const withAllPendingChanges = selectedAusType === AusType.DU_LP_PENDING_CHANGES;

      return {
        ...common,
        runAusSubmitType: AusType.DU_LP,
        withAllPendingChanges,
      };
    }

    case LoanEditorActionsEnum.SELECT_AUS_PROVIDER: {
      return { ...common, selectAusProviderType: formItem.ausProvider?.[0].value };
    }

    case LoanEditorActionsEnum.RESET_AUS_FINDINGS: {
      return { ...common, ausTypeToReset: formItem.resetAusProvider?.[0]?.value };
    }

    case LoanEditorActionsEnum.OPEN_LOAN_EDITOR_FORM: {
      return { ...common, customFormId: formItem.loanEditorCustomForm?.[0]?.value };
    }

    case LoanEditorActionsEnum.OPEN_URL_IN_IFRAME: {
      const item = formItem.openUrlInIframe?.[0] ?? {};

      return {
        ...common,
        url: item.url,
        title: item.title || null,
        size: getOptionValue(item.size) ?? item.size ?? null,
      };
    }

    case LoanEditorActionsEnum.ASSIGN_LOAN_ROLE:

    case LoanEditorActionsEnum.UNASSIGN_LOAN_ROLE: {
      const { loanRole, allowUserSelection } = formItem.loanRole[0];
      const isAssign = type === LoanEditorActionsEnum.ASSIGN_LOAN_ROLE;

      return {
        ...common,
        loanRoleId: loanRole.value,
        criteria: isAssign && allowUserSelection ? null : loanRoleCriteria,
        ...getConditionalObject(isAssign, {
          allowUserSelection,
        }),
      };
    }

    case LoanEditorActionsEnum.ASSIGN_CHAT_MEMBERS:

    case LoanEditorActionsEnum.UNASSIGN_CHAT_MEMBERS: {
      const { value } = formItem.chatCriteria[0].chatCriteria;

      return {
        ...common,
        chatType: value,
        criterion: chatCriteria,
      };
    }

    case LoanEditorActionsEnum.ASSIGN_TASK: {
      const [assignTaskItem] = formItem.assignTask;
      const { taskTypeId } = assignTaskItem.taskType;

      return {
        ...common,
        taskTypeId,
        formId: assignTaskItem.loanForm?.value || null,
        borrowerTargets: getAssignTaskBorrowerTargetsApiFormat(assignTaskItem),
      };
    }

    case LoanEditorActionsEnum.SUBMIT_UCD: {
      const { value } = formItem.submitUcd[0].runUcdSubmitType;

      return {
        ...common,
        runUcdSubmitType: value,
      };
    }

    case LoanEditorActionsEnum.RUN_LOAN_CALCULATIONS: {
      const runLoanCalculationsItem = formItem.runLoanCalculations?.[0];

      return {
        ...common,
        saveMode:
          runLoanCalculationsItem?.saveMode?.value ??
          defaultRunLoanCalculationsSaveModeOption.value,
        saveWithoutProgramWhenInvalid: !!runLoanCalculationsItem?.saveWithoutProgramWhenInvalid,
      };
    }

    case LoanEditorActionsEnum.CREATE_AND_DOWNLOAD_MISMO_FILE: {
      const { value } = formItem.mismoDocument[0].documentType;

      return {
        ...common,
        documentType: value,
      };
    }

    case LoanEditorActionsEnum.EXPORT_LOAN_PACKAGE: {
      const { includeDocuments, includeMismo } = formItem.exportLoanPackage[0];

      return {
        ...common,
        includeDocuments: !!includeDocuments,
        includeMismo: !!includeMismo,
      };
    }

    case LoanEditorActionsEnum.SEND_EMAIL_TEMPLATE:
      return getSendEmailTemplateApiFormat({ formItem, common, dynamicFieldsOptions });

    case LoanEditorActionsEnum.ADD_CONDITION: {
      const conditions = formItem.conditions.map(
        ({ conditionId, priorToType, borrowerMessage, number, taskTypes }) => {
          return {
            conditionId: conditionId.value,
            ...getPriorToRequestFields(priorToType),
            borrowerText: borrowerMessage,
            number,
            tasks: taskTypes.map(({ taskTypeId, formBundleId, isFormRequired }) => ({
              taskTypeId,
              formBundleId: isFormRequired ? formBundleId.value : null,
            })),
          };
        },
      );

      return {
        ...common,
        conditions,
      };
    }

    case LoanEditorActionsEnum.GRANT_CLOSING_PORTAL_ACCESS: {
      return {
        ...common,
        email: closingPortalEmailExpression || '',
      };
    }

    case LoanEditorActionsEnum.SAVE_PENDING_CHANGES: {
      return {
        ...common,
        permissions: savePendingChangesPermissions || [],
      };
    }

    case LoanEditorActionsEnum.SET_APPLICATION_IMPOUND: {
      return getApplicationImpoundApiFormat({
        formItem,
        common,
        buildExpressionTree,
        arithmeticOperationsSchemaParams,
        isNewExpressionsParsingLogicEnabled,
      });
    }

    case LoanEditorActionsEnum.RUN_FHA_SERVICE: {
      const fhaItem = formItem.runFhaService[0];
      const serviceTypeValue = fhaItem.serviceType?.value;
      const modeValue = fhaItem.mode?.value;
      const hasModes = !!fhaServiceTypeModeOptionsMap[serviceTypeValue];

      return {
        ...common,
        serviceType: {
          type: serviceTypeValue,
          ...(hasModes ? { mode: modeValue } : {}),
        },
      };
    }

    case LoanEditorActionsEnum.RUN_VA_SERVICE: {
      const vaItem = formItem.runVaService[0];

      return {
        ...common,
        serviceType: {
          type: vaItem.serviceType?.value,
        },
      };
    }

    case LoanEditorActionsEnum.RUN_FLOOD_SERVICE: {
      const floodItem = formItem.runFloodService[0];
      const kind = floodItem.kind?.value;
      const isDetermination = floodServiceDeterminationKinds.includes(kind);
      const isDeterminationKind = kind === FloodServiceKind.DETERMINATION;
      const promptAtExecution = isDeterminationKind && !!floodItem.promptAtExecution;
      // When promptAtExecution is set, rush/product override are decided at execution time, so the
      // configured defaults are sent as null rather than false/empty.
      const configuredRush = promptAtExecution ? null : !!floodItem.rush;

      // Rush + the pending task apply only to an order (Determination / Life-of-Loan Upgrade); the FloodCheck
      // pre-screen and the Revision re-query are fire-and-forget. Product override is Determination-only.
      return {
        ...common,
        kind,
        rush: isDetermination ? configuredRush : false,
        pendingTaskTypeId: isDetermination ? (floodItem.pendingTaskType?.taskTypeId ?? null) : null,
        productOverrideKey:
          isDeterminationKind && !promptAtExecution
            ? (floodItem.productOverride?.value ?? null)
            : null,
        promptAtExecution,
      };
    }

    case LoanEditorActionsEnum.UPDATE_FLOOD_DETERMINATION:
      return { ...common, kind: formItem.updateFloodDetermination[0].kind?.value };

    case LoanEditorActionsEnum.RUN_APPRAISAL_SERVICE: {
      const appraisalItem = formItem.runAppraisalService[0];
      const operationType = appraisalItem.operation?.value;

      const buildOperation = () => {
        switch (operationType) {
          case AppraisalOperationType.SUBMIT: {
            const supportingDocuments = toAppraisalSupportingDocuments(
              appraisalItem.supportingDocuments,
            );

            return {
              type: AppraisalOperationType.SUBMIT,
              mode: appraisalItem.resubmitMode?.value,
              dueDateBusinessDays: toAppraisalDueDateBusinessDays(
                appraisalItem.dueDateBusinessDays,
              ),
              ...getConditionalObject(supportingDocuments.length > 0, { supportingDocuments }),
            };
          }

          case AppraisalOperationType.SEND_PAYMENT_APP:
            return {
              type: AppraisalOperationType.SEND_PAYMENT_APP,
              paymentTaskTypeId: appraisalItem.paymentTaskType?.taskTypeId,
            };

          case AppraisalOperationType.DENY_FEE_ESCALATION:

          case AppraisalOperationType.ACCEPT_FEE_ESCALATION: {
            const promptForEscalationIdAtExecution =
              !!appraisalItem.promptForEscalationIdAtExecution;

            return {
              type: operationType,
              promptForEscalationIdAtExecution,
            };
          }

          case AppraisalOperationType.HOLD: {
            const promptForReasonAtExecution = !!appraisalItem.promptForReasonAtExecution;
            const reason = appraisalItem.reason?.trim();

            return {
              type: AppraisalOperationType.HOLD,
              promptForReasonAtExecution,
              ...getConditionalObject(!promptForReasonAtExecution && !!reason, () => ({ reason })),
            };
          }

          case AppraisalOperationType.CANCEL:
          case AppraisalOperationType.UPDATE:
          case AppraisalOperationType.UNHOLD:
          case AppraisalOperationType.OPEN_LOAN_FILE:
          case AppraisalOperationType.OPEN_ORDER:
            return { type: operationType };

          case AppraisalOperationType.REQUEST_REVISION:
            return appraisalItem.allowManualSelection
              ? {
                  type: AppraisalOperationType.REQUEST_REVISION,
                  allowManualSelection: true,
                }
              : {
                  type: AppraisalOperationType.REQUEST_REVISION,
                  title: appraisalItem.title,
                  description: appraisalItem.description,
                  allowManualSelection: false,
                };

          default:
            return null;
        }
      };

      return {
        ...common,
        operation: buildOperation(),
      };
    }

    case LoanEditorActionsEnum.RUN_APPRAISAL_SERVICE_QC: {
      const appraisalQcItem = formItem.runAppraisalServiceQc[0];
      const operationType = appraisalQcItem.operation?.value;
      const supportingDocuments = toHomeVisionSupportingDocuments(
        appraisalQcItem.supportingDocuments,
      );

      return {
        ...common,
        operation:
          operationType === AppraisalQcOperationType.SUBMIT_FOR_QC
            ? {
                type: operationType,
                mode: appraisalQcItem.resubmitMode?.value,
                useJobType: appraisalQcItem.useJobType !== false,
                ...getConditionalObject(supportingDocuments.length > 0, { supportingDocuments }),
              }
            : { type: operationType },
      };
    }

    case LoanEditorActionsEnum.REQUEST_ARGYLE_VERIFICATION: {
      const { duplicatePolicy, sendNotification } = formItem.requestArgyleVerification[0];

      return {
        ...common,
        duplicatePolicy: duplicatePolicy?.value,
        sendNotification: sendNotification ?? true,
      };
    }

    case LoanEditorActionsEnum.REQUEST_FINICITY_VERIFICATION: {
      const { duplicatePolicy, sendNotification } = formItem.requestFinicityVerification[0];

      return {
        ...common,
        duplicatePolicy: duplicatePolicy?.value,
        sendNotification: sendNotification ?? true,
      };
    }

    case LoanEditorActionsEnum.ASSIGN_SUB_COMPANY_AE:
    case LoanEditorActionsEnum.DECIDE_CERTIFICATION:
    case LoanEditorActionsEnum.EXECUTE_AI_PROCESS:
      return getSubCompanyActionApiFormat({ type, formItem, common });

    case LoanEditorActionsEnum.ADD_HALCYON_TRANSCRIPT_FORMS: {
      const { forms, yearMode, specificYears, allowManualSelection } = formItem.halcyonAddForms[0];

      if (allowManualSelection) {
        return { ...common, formYearRules: null, allowManualSelection: true };
      }

      return {
        ...common,
        formYearRules: [
          {
            expression: null,
            userFriendlyExpression: null,
            forms: expandHalcyonFormsToApiFormat(forms || [], yearMode, specificYears),
          },
        ],
        allowManualSelection: false,
      };
    }

    case LoanEditorActionsEnum.CALL_INTEGRATION:
      return getCallIntegrationApiFormat(formItem, common);

    case LoanEditorActionsEnum.ORDER_MORTGAGE_INSURANCE:
    case LoanEditorActionsEnum.CHECK_MORTGAGE_INSURANCE_STATUS:
    case LoanEditorActionsEnum.ACTIVATE_MORTGAGE_INSURANCE:
      return getMortgageInsuranceApiFormat(formItem, common);

    case LoanEditorActionsEnum.RUN_AI_PROCESS:
    case LoanEditorActionsEnum.SEND_AI_PROCESS_RESULTS:
      return getAiProcessActionApiFormat(formItem, common);

    default:
      logger.error('Missed handler for loan/automated action type: ', type);

      return null;
  }
};

const getRunLoanCalculationsFormItems = actionItem => [
  {
    saveMode:
      runLoanCalculationsSaveModeOptions.find(({ value }) => value === actionItem.saveMode) ??
      defaultRunLoanCalculationsSaveModeOption,
    saveWithoutProgramWhenInvalid: !!actionItem.saveWithoutProgramWhenInvalid,
  },
];

const buildFloodElevationCertificateReportItem = (
  reportOption,
  provider,
  options,
  floodProductOptions,
) => {
  const floodElevationCertificateProviderOption = floodElevationCertificateProviderOptions.find(
    ({ value }) => value === provider,
  );
  const isServiceLink =
    provider === FloodElevationCertificateProviderType.SERVICE_LINK &&
    options?.type === FloodElevationCertificateProviderType.SERVICE_LINK;

  return {
    report: reportOption,
    floodElevationCertificateProvider: floodElevationCertificateProviderOption,
    ...getConditionalObject(isServiceLink, () => ({
      serviceLinkOperation:
        floodServiceOperationOptions.find(({ value }) => value === options.operation) ??
        defaultFloodServiceOperationOption,
      serviceLinkProductOverride:
        floodProductOptions.find(({ value }) => value === options.productOverrideKey) ??
        COMPANY_DEFAULT_FLOOD_PRODUCT_OPTION,
      serviceLinkRush: !!options.rush,
      serviceLinkAllowManualSelection: !!options.allowManualSelection,
    })),
  };
};

// A missing `mode` on a legacy config resolves to the same concrete per-execution-path default the
// server applies (manual → resubmit to existing, automated → submit once), rather than a blank Select.
const resolveHomeVisionResubmitMode = (apiOperation, isAutomatedActions) =>
  apiOperation?.type === AppraisalQcOperationType.SUBMIT_FOR_QC
    ? (homeVisionResubmitModeOptions.find(({ value }) => value === apiOperation.mode) ??
      getDefaultHomeVisionResubmitModeOption(isAutomatedActions))
    : null;

// A config saved before the job-type toggle existed has no `useJobType`; the server treats that as on.
const resolveHomeVisionUseJobType = apiOperation =>
  apiOperation?.type === AppraisalQcOperationType.SUBMIT_FOR_QC
    ? apiOperation.useJobType !== false
    : null;

// Supporting documents are keyed by document type NAME (ids differ between environments). A saved name
// that is not among the current options still round-trips, so re-saving never drops a configured row.
const toHomeVisionSupportingDocuments = rows =>
  (rows ?? [])
    .filter(({ documentType, homeVisionDocType }) => documentType?.name && homeVisionDocType?.value)
    .map(({ documentType, homeVisionDocType }) => ({
      documentTypeName: documentType.name,
      homeVisionDocType: homeVisionDocType.value,
    }));

const toAppraisalDueDateBusinessDays = value =>
  _.isNil(value) || value === '' ? DEFAULT_APPRAISAL_DUE_DATE_BUSINESS_DAYS : Number(value);

// A Submit saved before the due-date input existed has no `dueDateBusinessDays`; the server uses the default.
const resolveAppraisalDueDateBusinessDays = apiOperation =>
  apiOperation?.type === AppraisalOperationType.SUBMIT
    ? (apiOperation.dueDateBusinessDays ?? DEFAULT_APPRAISAL_DUE_DATE_BUSINESS_DAYS)
    : null;

const toAppraisalSupportingDocuments = rows =>
  (rows ?? [])
    .filter(({ documentType }) => documentType?.name)
    .map(({ documentType }) => ({ documentTypeName: documentType.name }));

const resolveSupportingDocumentType = (documentTypeName, documentTypesOptions) =>
  documentTypesOptions.find(
    ({ name }) => name?.toLowerCase() === documentTypeName?.toLowerCase(),
  ) ?? {
    name: documentTypeName,
    value: documentTypeName,
  };

const resolveHomeVisionSupportingDocType = homeVisionDocType =>
  homeVisionSupportingDocTypeOptions.find(({ value }) => value === homeVisionDocType) ?? {
    name: homeVisionDocType,
    value: homeVisionDocType,
  };

const resolveHomeVisionSupportingDocuments = (apiOperation, documentTypesOptions) =>
  apiOperation?.type === AppraisalQcOperationType.SUBMIT_FOR_QC
    ? (apiOperation.supportingDocuments ?? []).map(({ documentTypeName, homeVisionDocType }) =>
        extendObjWithUUID({
          documentType: resolveSupportingDocumentType(documentTypeName, documentTypesOptions),
          homeVisionDocType: resolveHomeVisionSupportingDocType(homeVisionDocType),
        }),
      )
    : [];

const resolveAppraisalSupportingDocuments = (apiOperation, documentTypesOptions) =>
  apiOperation?.type === AppraisalOperationType.SUBMIT
    ? (apiOperation.supportingDocuments ?? []).map(({ documentTypeName }) =>
        extendObjWithUUID({
          documentType: resolveSupportingDocumentType(documentTypeName, documentTypesOptions),
        }),
      )
    : [];

const getAppraisalFormItem = (apiOperation, taskTypes, documentTypesOptions) => {
  const isRequestRevision = apiOperation?.type === AppraisalOperationType.REQUEST_REVISION;
  const isHold = apiOperation?.type === AppraisalOperationType.HOLD;

  return {
    operation: appraisalOperationOptions.find(({ value }) => value === apiOperation?.type) ?? null,
    resubmitMode:
      apiOperation?.type === AppraisalOperationType.SUBMIT
        ? (appraisalResubmitModeOptions.find(({ value }) => value === apiOperation.mode) ?? null)
        : null,
    dueDateBusinessDays: resolveAppraisalDueDateBusinessDays(apiOperation),
    paymentTaskType:
      apiOperation?.type === AppraisalOperationType.SEND_PAYMENT_APP
        ? (taskTypes[apiOperation.paymentTaskTypeId] ?? null)
        : null,
    title: isRequestRevision ? (apiOperation.title ?? null) : null,
    description: isRequestRevision ? (apiOperation.description ?? null) : null,
    allowManualSelection: isRequestRevision ? !!apiOperation.allowManualSelection : false,
    reason: isHold ? (apiOperation.reason ?? null) : null,
    promptForReasonAtExecution: isHold ? !!apiOperation.promptForReasonAtExecution : false,
    promptForEscalationIdAtExecution: !!apiOperation?.promptForEscalationIdAtExecution,
    supportingDocuments: resolveAppraisalSupportingDocuments(apiOperation, documentTypesOptions),
  };
};

const getAppraisalQcFormItem = (apiOperation, isAutomatedActions, documentTypesOptions = []) => ({
  operation: appraisalQcOperationOptions.find(({ value }) => value === apiOperation?.type) ?? null,
  resubmitMode: resolveHomeVisionResubmitMode(apiOperation, isAutomatedActions),
  useJobType: resolveHomeVisionUseJobType(apiOperation),
  supportingDocuments: resolveHomeVisionSupportingDocuments(apiOperation, documentTypesOptions),
});

export const getFormActionFormatFromApiFormat =
  ({
    fieldsMap,
    actionTypeOptions,
    tagsOptions = [],
    checklistsOptions = [],
    customFieldDefinitionOptions = [],
    formsOptions = [],
    formBundlesOptions = [],
    documentTypesOptions = [],
    reportsOptions = [],
    fieldsOptions = [],
    runAusOptions = [],
    ausProviderOptions = [],
    resetAusProviderOptions = [],
    loanRoleOptions = [],
    securityGroupOptions = [],
    chatTypeOptions = [],
    taskTypes = {},
    formListOptions = [],
    slaGroupOptions = [],
    emailTemplatesOptions = [],
    dynamicFieldsOptions = [],
    priorToOptions = [],
    loanActionsConditions = {},
    halcyonFormOptions = [],
    loanEditorCustomFormsOptions = [],
    usersOptions = [],
    floodProductOptions = [],
    impoundTypeOptions = [],
    isAutomatedActions = false,
  }) =>
  actionItem => {
    const itemType = actionTypeOptions.find(({ value }) => value === actionItem.type);
    const timedSequenceOptions = getConditionalObject(!!actionItem.timedSequenceOptions, {
      ...actionItem.timedSequenceOptions,
      isCustomStopExpression: !!actionItem.timedSequenceOptions?.stopExpression?.value,
      userFriendlyStopExpression:
        actionItem.timedSequenceOptions?.stopExpression?.userFriendlyValue,
    });

    if (PAYLOAD_LESS_ACTION_TYPES.has(actionItem.type)) {
      return { itemType, timedSequenceOptions };
    }

    const getExtractedFormFormat = EXTRACTED_FORM_FORMAT_GETTERS[actionItem.type];

    if (getExtractedFormFormat) {
      return getExtractedFormFormat(actionItem, itemType, timedSequenceOptions, {
        documentTypesOptions,
        taskTypes,
      });
    }

    switch (actionItem.type) {
      case LoanEditorActionsEnum.ADD_TAG:

      case LoanEditorActionsEnum.REMOVE_TAG: {
        const tags = actionItem.tagIds.map(tagId =>
          tagsOptions.find(({ value }) => value === tagId),
        );

        return { itemType, timedSequenceOptions, tags };
      }

      case LoanEditorActionsEnum.GENERATE_CHECKLIST: {
        const checklists = actionItem.checklistIds.map(checklistId =>
          checklistsOptions.find(({ value }) => value === checklistId),
        );

        return { itemType, timedSequenceOptions, checklists };
      }

      case LoanEditorActionsEnum.GENERATE_FORM:

      case LoanEditorActionsEnum.GENERATE_AND_SAVE_FORM: {
        const forms = actionItem.formIds.map(id => ({
          ...formsOptions.find(({ value }) => value === id),
          isTest: false,
        }));

        return { itemType, timedSequenceOptions, forms };
      }

      case LoanEditorActionsEnum.GENERATE_PDF: {
        return {
          itemType,
          timedSequenceOptions,
          pdf: getPdfSourceFormItem({ actionItem, formsOptions, formBundlesOptions }),
        };
      }

      case LoanEditorActionsEnum.CREATE_DOCUMENT_FROM_SOURCE: {
        return {
          itemType,
          timedSequenceOptions,
          createDocumentFromSource: getCreateDocumentFromSourceFormItem({
            actionItem,
            formsOptions,
            formBundlesOptions,
            documentTypesOptions,
          }),
        };
      }

      case LoanEditorActionsEnum.SET_FORM_BUNDLE_READINESS_STATUS: {
        return {
          itemType,
          timedSequenceOptions,
          setFormBundleReadinessStatus: getSetFormBundleReadinessStatusFormItem({
            actionItem,
            formBundlesOptions,
          }),
        };
      }

      case LoanEditorActionsEnum.SEND_FORM: {
        const forms = actionItem.forms.map(({ id, isTest }) => ({
          ...formsOptions.find(({ value }) => value === id),
          isTest: !!isTest,
        }));

        return { itemType, timedSequenceOptions, forms };
      }

      case LoanEditorActionsEnum.SEND_FORM_BUNDLE: {
        const formBundles = actionItem.formBundles.map(
          ({ id, isTest, requiresApproval, previewType }) => ({
            ...formBundlesOptions.find(({ value }) => value === id),
            isTest,
            requiresApproval: !!requiresApproval,
            enablePreview: previewType?.type === 'Form',
            formForPreview: formsOptions.find(({ value }) => value === previewType?.formId),
          }),
        );

        return { itemType, timedSequenceOptions, formBundles };
      }

      case LoanEditorActionsEnum.MAIL_DOCUMENT_PACKAGE: {
        return {
          itemType,
          timedSequenceOptions,
          mailDocumentPackage: getMailDocumentPackageFormItem({ actionItem, formBundlesOptions }),
        };
      }

      case LoanEditorActionsEnum.UPLOAD_FORM_DELIVERY: {
        return {
          itemType,
          timedSequenceOptions,
          uploadFormDelivery: getUploadFormDeliveryFormItem({ actionItem, formBundlesOptions }),
        };
      }

      case LoanEditorActionsEnum.ORDER_UPDATE_REPORT_AUTO:

      case LoanEditorActionsEnum.ORDER_UPDATE_REPORT_MANUAL: {
        const reports = actionItem.reportTypes.map(
          ({
            type,
            vendorOptions,
            reportType,
            provider,
            options,
            method,
            formYearRules,
            borrowerTarget,
            allowManualSelection,
            resubmitMode,
            authorizationSource,
            useNonProductionCredentials,
            isPreAuthorization,
            bureaus,
            pullType,
          }) => {
            if (type.includes(CreditReportField.value)) {
              return getCreditReportFormItem({
                type,
                bureaus,
                pullType,
                allowManualSelection,
                reportsOptions,
              });
            }

            const reportOption = reportsOptions.find(({ value }) => value === type);

            if (type === ReportTypeEnum.AVM) {
              return {
                report: reportOption,
                ...getConditionalObject(vendorOptions, () => ({
                  vendorOptions: {
                    ...vendorOptions,
                    orderedCascadeProviders:
                      vendorOptions?.orderedCascadeProviders?.map(
                        value => cascadeProviderMap[value],
                      ) ?? [],
                    vendorType: avmVendorTypeMap[vendorOptions?.type],
                  },
                })),
              };
            }

            if (type === ReportTypeEnum.ARGYLE_VERIFICATION) {
              const argyleReportTypeOption = argyleReportTypeOptions.find(
                ({ value }) => value === reportType,
              );

              return {
                report: reportOption,
                argyleReportType: argyleReportTypeOption,
              };
            }

            if (type === ReportTypeEnum.FLOOD_ELEVATION_CERTIFICATE) {
              return buildFloodElevationCertificateReportItem(
                reportOption,
                provider,
                options,
                floodProductOptions,
              );
            }

            if (type === ReportTypeEnum.VERIFICATION_OF_ASSETS) {
              const verificationOfAssets = verificationOfAssetsOptions.find(
                ({ value }) => value === provider?.type,
              );

              return {
                report: reportOption,
                verificationOfAssets,
              };
            }

            if (type === ReportTypeEnum.HALCYON_TAX_TRANSCRIPT) {
              const apiRules = formYearRules || [];
              const baseApiRule = apiRules.find(r => _.isNil(r.userFriendlyExpression));
              const conditionalApiRules = apiRules.filter(r => !_.isNil(r.userFriendlyExpression));

              const parsedBase = baseApiRule
                ? parseHalcyonRuleFormsToFormState(baseApiRule, halcyonFormOptions)
                : { forms: [], yearMode: null, specificYears: [] };

              const parsedConditionalRules = conditionalApiRules.map(r => {
                const parsed = parseHalcyonRuleFormsToFormState(r, halcyonFormOptions);

                return {
                  expression: r.userFriendlyExpression || '',
                  forms: parsed.forms,
                  yearMode: parsed.yearMode,
                  specificYears: parsed.specificYears,
                };
              });

              const halcyonResubmitMode =
                halcyonResubmitModeOptions.find(({ value }) => value === resubmitMode) ??
                defaultHalcyonResubmitModeOption;

              const halcyonAuthorizationSource =
                halcyonAuthorizationSourceOptions.find(
                  ({ value }) => value === authorizationSource,
                ) ?? defaultHalcyonAuthorizationSourceOption;

              return {
                report: reportOption,
                halcyonMethod: halcyonMethodOptions.find(({ value }) => value === method),
                halcyonBaseForms: parsedBase.forms,
                halcyonBaseYearMode: parsedBase.yearMode,
                halcyonBaseSpecificYears: parsedBase.specificYears,
                halcyonUseConditionalRules: !!conditionalApiRules.length,
                halcyonConditionalRules: parsedConditionalRules,
                halcyonBorrowerTarget: halcyonBorrowerTargetOptions.find(
                  ({ value }) => value === borrowerTarget?.type,
                ),
                halcyonBorrowerExpression: borrowerTarget?.userFriendlyExpression || '',
                halcyonAllowManualSelection: !!allowManualSelection,
                halcyonResubmitMode,
                halcyonAuthorizationSource,
                halcyonUseNonProdCredentials: !!useNonProductionCredentials,
                halcyonIsPreAuthorization: !!isPreAuthorization,
              };
            }

            return { report: reportOption };
          },
        );

        return { itemType, timedSequenceOptions, reports };
      }

      case LoanEditorActionsEnum.SET_FIELD_STATIC: {
        const fields = actionItem.changes.map(
          ({ fieldId, type, newValue, sourceFieldId, sequenceKey, digitCount, seed }) => {
            const isStaticValue = type === LoanActionStaticItemsTypesEnum.STATIC_VALUE;
            const isCurrentDate = type === LoanActionStaticItemsTypesEnum.CURRENT_DATE;
            const isNextSequence = type === LoanActionStaticItemsTypesEnum.NEXT_SEQUENCE_VALUE;
            const fieldData = fieldsMap[fieldId] || {};
            const isDateType = fieldData.type === FieldTypesEnum.DATE;
            const dateTypeValue = isCurrentDate
              ? LoanActionDateTypesEnum.EXECUTION_DATE
              : LoanActionDateTypesEnum.SELECTED_DATE;
            const formattedFieldValue = !isStaticValue
              ? undefined
              : formatActionItemsFieldsInitialFormValues({
                  fieldValue: newValue,
                  valueData: fieldData,
                });

            const getValueSource = () => {
              if (isStaticValue || isCurrentDate) {
                return LoanActionStaticValueSourceEnum.VALUE;
              }

              if (isNextSequence) {
                return LoanActionStaticValueSourceEnum.NEXT_SEQUENCE;
              }

              return LoanActionStaticValueSourceEnum.FIELD;
            };

            return {
              field: fieldsOptions.find(({ value }) => value === fieldId),
              valueSource: getValueSource(),
              value: formattedFieldValue,
              dateType: isDateType ? dateTypeValue : null,
              sourceField:
                type === LoanActionStaticItemsTypesEnum.SOURCE_FIELD
                  ? fieldsOptions.find(({ value }) => value === sourceFieldId)
                  : null,
              sequenceKey: isNextSequence ? sequenceKey : undefined,
              digitCount: isNextSequence ? digitCount : undefined,
              seed: isNextSequence ? seed : undefined,
            };
          },
        );

        return { itemType, timedSequenceOptions, fields };
      }

      case LoanEditorActionsEnum.SET_FIELD_DYNAMIC: {
        const variables = actionItem.changes.map(({ fieldId, name, required }) => {
          return {
            field: fieldsOptions.find(({ value }) => value === fieldId),
            isRequired: required,
            name,
          };
        });

        return { itemType, timedSequenceOptions, variables };
      }

      case LoanEditorActionsEnum.ENTER_CUSTOM_FIELDS: {
        const selectedDefinitions = (actionItem.customFieldDefinitionIds ?? [])
          .map(id => customFieldDefinitionOptions.find(option => option.value === id))
          .filter(Boolean);
        const enterCustomFields = [{ customFieldDefinitionIds: selectedDefinitions }];

        return { itemType, timedSequenceOptions, enterCustomFields };
      }

      case LoanEditorActionsEnum.RUN_AUS: {
        const targetAusType = actionItem.withAllPendingChanges
          ? AusType.DU_LP_PENDING_CHANGES
          : AusType.DU_LP;

        const ausType = runAusOptions.find(({ value }) => value === targetAusType);
        const runAusSubmit = [ausType];

        return { itemType, timedSequenceOptions, runAusSubmit };
      }

      case LoanEditorActionsEnum.SELECT_AUS_PROVIDER: {
        const ausProviderType = ausProviderOptions.find(
          ({ value }) => value === actionItem.selectAusProviderType,
        );
        const ausProvider = [ausProviderType];

        return { itemType, timedSequenceOptions, ausProvider };
      }

      case LoanEditorActionsEnum.RESET_AUS_FINDINGS: {
        const resetAusProviderType = resetAusProviderOptions.find(
          ({ value }) => value === actionItem.ausTypeToReset,
        );
        const resetAusProvider = [resetAusProviderType];

        return { itemType, timedSequenceOptions, resetAusProvider };
      }

      case LoanEditorActionsEnum.OPEN_LOAN_EDITOR_FORM: {
        const customForm = loanEditorCustomFormsOptions.find(
          ({ value }) => value === actionItem.customFormId,
        );
        const loanEditorCustomForm = [customForm];

        return { itemType, timedSequenceOptions, loanEditorCustomForm };
      }

      case LoanEditorActionsEnum.OPEN_URL_IN_IFRAME: {
        const size =
          iframeSizeOptions.find(({ value }) => value === actionItem.size) ??
          actionItem.size ??
          null;
        const openUrlInIframe = [
          {
            url: actionItem.url ?? '',
            title: actionItem.title ?? '',
            size,
          },
        ];

        return { itemType, timedSequenceOptions, openUrlInIframe };
      }

      case LoanEditorActionsEnum.ASSIGN_LOAN_ROLE:

      case LoanEditorActionsEnum.UNASSIGN_LOAN_ROLE: {
        const isAssign = actionItem.type === LoanEditorActionsEnum.ASSIGN_LOAN_ROLE;
        const loanRole = [
          {
            loanRole: loanRoleOptions.find(({ value }) => value === actionItem.loanRoleId),
            loanRoleCriteriaFriendlyExpression: actionItem.criteria?.userFriendlyValue || '',
            ...getConditionalObject(isAssign, {
              allowUserSelection: actionItem.allowUserSelection,
            }),
          },
        ];

        return { itemType, timedSequenceOptions, loanRole };
      }

      case LoanEditorActionsEnum.RUN_LOAN_CALCULATIONS: {
        return {
          itemType,
          timedSequenceOptions,
          runLoanCalculations: getRunLoanCalculationsFormItems(actionItem),
        };
      }

      case LoanEditorActionsEnum.SUBMIT_UCD: {
        const submitUcd = [
          {
            runUcdSubmitType: ucdTypeOptions.find(
              ({ value }) => value === actionItem.runUcdSubmitType,
            ),
          },
        ];

        return { itemType, timedSequenceOptions, submitUcd };
      }

      case LoanEditorActionsEnum.CREATE_AND_DOWNLOAD_MISMO_FILE: {
        const mismoDocument = getMismoDocumentFormItem(actionItem);

        return { itemType, timedSequenceOptions, mismoDocument };
      }

      case LoanEditorActionsEnum.EXPORT_LOAN_PACKAGE: {
        const exportLoanPackage = [
          {
            includeDocuments: actionItem.includeDocuments ?? true,
            includeMismo: actionItem.includeMismo ?? true,
          },
        ];

        return { itemType, timedSequenceOptions, exportLoanPackage };
      }

      case LoanEditorActionsEnum.ASSIGN_CHAT_MEMBERS:

      case LoanEditorActionsEnum.UNASSIGN_CHAT_MEMBERS: {
        const chatCriteria = [
          {
            chatCriteria: chatTypeOptions.find(({ value }) => value === actionItem.chatType),
            chatCriteriaFriendlyExpression: actionItem.criterion?.userFriendlyValue || '',
          },
        ];

        return { itemType, timedSequenceOptions, chatCriteria };
      }

      case LoanEditorActionsEnum.ASSIGN_TASK: {
        const taskType = taskTypes[actionItem.taskTypeId];
        const borrowerTargets = getAssignTaskBorrowerTargetsFormFormat(
          taskType,
          actionItem.borrowerTargets,
        );
        const isSlaGroupsAssigneeSelected =
          taskType?.taskAssignmentType === TaskAssignmentType.SLA_GROUP;
        const assignTask = [
          {
            taskType,
            assignTo: taskAssignmentTypeOptions.find(
              ({ value }) => value === taskType?.taskAssignmentType,
            ),
            entityAssignmentType: getAssignTaskEntityAssignmentTypeOption(
              taskType,
              borrowerTargets,
            ),
            borrowerTargets,
            loanForm: actionItem.formId
              ? formListOptions.find(({ value }) => value === actionItem.formId)
              : null,
            slaGroup: isSlaGroupsAssigneeSelected
              ? slaGroupOptions.find(({ value }) => value === taskType?.slaGroupId)
              : null,
          },
        ];

        return { itemType, timedSequenceOptions, assignTask };
      }

      case LoanEditorActionsEnum.SEND_EMAIL_TEMPLATE: {
        const {
          templateId,
          templateCategoryId,
          trackingStatus,
          subject,
          bccList,
          ccList,
          sender,
          receivers,
        } = actionItem.emailTemplateData || {};

        const loanContactTypesOptionsMap = _.keyBy(
          [...loanContactTypesOptions, ...loanRoleOptions, ...securityGroupOptions],
          'value',
        );

        const loanSenderTypesOptionsMap = _.keyBy(
          [...loanSenderTypesOptions, ...loanRoleOptions],
          'value',
        );

        const emailTemplate = emailTemplatesOptions.find(({ id }) => id === templateId);
        const bccListItems = bccList.map(contact =>
          getContactFormFormatFromApiFormat(contact, loanContactTypesOptionsMap),
        );
        const ccListItems = ccList.map(contact =>
          getContactFormFormatFromApiFormat(contact, loanContactTypesOptionsMap),
        );
        const receiversListItems = receivers.map(contact =>
          getContactFormFormatFromApiFormat(contact, loanContactTypesOptionsMap),
        );
        const senderItem = getContactFormFormatFromApiFormat(sender, loanSenderTypesOptionsMap);

        const formattedSubject = replaceSpanWithBrackets(
          subject ? subject : emailTemplate?.subject,
          dynamicFieldsOptions,
        );

        const subCompanyRecipients = getSubCompanyRecipientsFormFormat(
          actionItem.subCompanyRecipients,
          securityGroupOptions,
        );

        const sendEmailTemplate = [
          {
            templateId: emailTemplate,
            templateCategoryId,
            trackingStatus,
            subject: formattedSubject,
            bccList: bccListItems,
            ccList: ccListItems,
            sender: senderItem,
            receivers: receiversListItems,
            ...getConditionalObject(!!subCompanyRecipients, { subCompanyRecipients }),
          },
        ];

        return {
          itemType,
          timedSequenceOptions,
          sendEmailTemplate,
        };
      }

      case LoanEditorActionsEnum.ADD_CONDITION: {
        const conditions = actionItem.conditions.map(condition => {
          const { borrowerText, conditionId, priorToType, priorToTypeId, tasks } = condition;
          const { taskTypes, ...currentCondition } = loanActionsConditions[conditionId];

          return {
            borrowerMessage: borrowerText,
            conditionId: { name: getConditionName(currentCondition), value: conditionId },
            priorToType: findPriorToOption({ options: priorToOptions, priorToTypeId, priorToType }),
            taskTypes: taskTypes.map(({ formBundleId, taskTypeId, ...rest }) => ({
              ...rest,
              taskTypeId,
              formBundleId: formsOptions.find(
                ({ value }) =>
                  value === tasks.find(({ taskTypeId: id }) => id === taskTypeId).formBundleId,
              ),
            })),
          };
        });

        return { itemType, timedSequenceOptions, conditions };
      }

      case LoanEditorActionsEnum.GRANT_CLOSING_PORTAL_ACCESS: {
        const emailValue = actionItem.email;
        const isExpressionEmail = _.isPlainObject(emailValue);

        return {
          itemType,
          timedSequenceOptions,
          grantClosingPortalAccess: [
            {
              emailFriendlyExpression: isExpressionEmail
                ? emailValue?.userFriendlyValue || ''
                : emailValue || '',
            },
          ],
        };
      }

      case LoanEditorActionsEnum.SAVE_PENDING_CHANGES:
        return getSavePendingChangesFormItem({ itemType, timedSequenceOptions, actionItem });

      case LoanEditorActionsEnum.SET_APPLICATION_IMPOUND: {
        const impoundRows = getApplicationImpoundFormItem({ actionItem, impoundTypeOptions });

        return { itemType, timedSequenceOptions, setApplicationImpound: impoundRows };
      }

      case LoanEditorActionsEnum.RUN_FHA_SERVICE:
        return getRunFhaServiceFormItem({
          actionItem,
          fhaServiceTypeOptions,
          fhaServiceTypeModeOptionsMap,
          itemType,
          timedSequenceOptions,
        });

      case LoanEditorActionsEnum.RUN_VA_SERVICE: {
        const apiServiceType = actionItem.serviceType;
        const serviceTypeOption = vaServiceTypeOptions.find(
          ({ value }) => value === apiServiceType?.type,
        );

        return {
          itemType,
          timedSequenceOptions,
          runVaService: [{ serviceType: serviceTypeOption }],
        };
      }

      case LoanEditorActionsEnum.RUN_FLOOD_SERVICE: {
        const kindOption =
          floodServiceKindOptions.find(({ value }) => value === actionItem.kind) ?? null;
        const isDetermination = floodServiceDeterminationKinds.includes(actionItem.kind);
        const isDeterminationKind = actionItem.kind === FloodServiceKind.DETERMINATION;

        return {
          itemType,
          timedSequenceOptions,
          runFloodService: [
            {
              kind: kindOption,
              rush: isDetermination && !!actionItem.rush,
              pendingTaskType: isDetermination
                ? (taskTypes[actionItem.pendingTaskTypeId] ?? null)
                : null,
              // A null/absent productOverrideKey means "company default" (not "no selection"), so it
              // must reverse to the visible Company-default option rather than a blank Select.
              productOverride: isDeterminationKind
                ? (floodProductOptions.find(
                    ({ value }) => value === actionItem.productOverrideKey,
                  ) ??
                  (_.isNil(actionItem.productOverrideKey)
                    ? COMPANY_DEFAULT_FLOOD_PRODUCT_OPTION
                    : null))
                : null,
              promptAtExecution: isDeterminationKind && !!actionItem.promptAtExecution,
            },
          ],
        };
      }

      case LoanEditorActionsEnum.RUN_APPRAISAL_SERVICE: {
        return {
          itemType,
          timedSequenceOptions,
          runAppraisalService: [
            getAppraisalFormItem(actionItem.operation, taskTypes, documentTypesOptions),
          ],
        };
      }

      case LoanEditorActionsEnum.RUN_APPRAISAL_SERVICE_QC: {
        return {
          itemType,
          timedSequenceOptions,
          runAppraisalServiceQc: [
            getAppraisalQcFormItem(actionItem.operation, isAutomatedActions, documentTypesOptions),
          ],
        };
      }

      case LoanEditorActionsEnum.REQUEST_ARGYLE_VERIFICATION: {
        return {
          itemType,
          timedSequenceOptions,
          requestArgyleVerification: [
            {
              duplicatePolicy:
                argyleDuplicatePolicyOptions.find(
                  ({ value }) => value === actionItem.duplicatePolicy,
                ) || null,
              sendNotification: actionItem.sendNotification ?? true,
            },
          ],
        };
      }

      case LoanEditorActionsEnum.REQUEST_FINICITY_VERIFICATION: {
        return {
          itemType,
          timedSequenceOptions,
          requestFinicityVerification: [
            {
              duplicatePolicy:
                finicityDuplicatePolicyOptions.find(
                  ({ value }) => value === actionItem.duplicatePolicy,
                ) || null,
              sendNotification: actionItem.sendNotification ?? true,
            },
          ],
        };
      }

      case LoanEditorActionsEnum.ADD_HALCYON_TRANSCRIPT_FORMS: {
        const baseApiRule = (actionItem.formYearRules || []).find(r =>
          _.isNil(r.userFriendlyExpression),
        );
        const parsedBase = baseApiRule
          ? parseHalcyonRuleFormsToFormState(baseApiRule, halcyonFormOptions)
          : { forms: [], yearMode: null, specificYears: [] };

        return {
          itemType,
          timedSequenceOptions,
          halcyonAddForms: [
            {
              forms: parsedBase.forms,
              yearMode: parsedBase.yearMode,
              specificYears: parsedBase.specificYears,
              allowManualSelection: !!actionItem.allowManualSelection,
            },
          ],
        };
      }

      case LoanEditorActionsEnum.ASSIGN_SUB_COMPANY_AE:
      case LoanEditorActionsEnum.DECIDE_CERTIFICATION:
      case LoanEditorActionsEnum.EXECUTE_AI_PROCESS:
        return getSubCompanyActionFormFormat({
          type: actionItem.type,
          actionItem,
          itemType,
          timedSequenceOptions,
          usersOptions,
        });

      case LoanEditorActionsEnum.UPDATE_FLOOD_DETERMINATION: {
        const kindOption =
          floodDeterminationUpdateKindOptions.find(({ value }) => value === actionItem.kind) ??
          null;

        return { itemType, timedSequenceOptions, updateFloodDetermination: [{ kind: kindOption }] };
      }

      default:
        logger.error('Missed handler for action type: ', actionItem.type);

        return null;
    }
  };
